import { useState } from "react";
import type { TFunction } from "i18next";
import { MapPin, Truck } from "lucide-react";
import { AppleMap } from "@/components/maps/AppleMap";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { CheckoutLocationEditor } from "@/components/checkout/CheckoutLocationEditor";
import { getDistanceUnitPlural } from "@/lib/api";
import type { PriceQuote } from "@/lib/bookings-api";
import type { RentalType } from "@/components/checkout/CheckoutRentalTypeEditor";

type CarInfo = {
  city: string;
  state: string;
  countryCode: string | null;
  imageUrl: string | null;
  latitude?: number | null;
  longitude?: number | null;
  deliveryEnabled?: boolean;
  selfDriveDeliveryEnabled?: boolean;
  deliveryMaxRadius?: number | null;
  deliveryAirports?: Array<{ airport_code: string; airport_name: string; flat_fee: number }>;
};

export function CheckoutDeliverySection({
  t,
  carInfo,
  quote,
  rentalType,
  airportCodeParam,
  deliveryEngaged,
  deliveryAddress,
  deliveryDistanceParam,
  deliveryLatitude,
  deliveryLongitude,
  onApplyLocation,
  collapsibleMap = false,
  actionClassName = "text-xs font-medium text-foreground underline",
}: {
  t: TFunction;
  carInfo: CarInfo | null;
  collapsibleMap?: boolean;
  actionClassName?: string;
  quote: PriceQuote;
  rentalType: RentalType;
  airportCodeParam: string | undefined;
  deliveryEngaged: boolean;
  deliveryAddress: string | null;
  deliveryDistanceParam: number | undefined;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  onApplyLocation: (next: {
    deliveryDistance: number | null;
    deliveryAirportCode: string | null;
    deliveryAddress: string | null;
    deliveryLatitude: number | null;
    deliveryLongitude: number | null;
  }) => void;
}) {
  const [mapOpen, setMapOpen] = useState(false);
  const serviceLocationRequired = rentalType === "chauffeured" || rentalType === "event";
  if (!carInfo || (!serviceLocationRequired && !(carInfo.city || carInfo.state))) return null;
  const typeDeliveryEnabled =
    rentalType === "driving"
      ? carInfo.selfDriveDeliveryEnabled === true
      : serviceLocationRequired;
  const currentDeliveryEngaged = deliveryEngaged && !!typeDeliveryEnabled;
  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {currentDeliveryEngaged && airportCodeParam ? (
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
          ) : (
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">{t("checkout.pickup_dropoff")}</p>
            {currentDeliveryEngaged ? (
              <>
                <p className="mt-1 break-words text-xs text-foreground">
                  {airportCodeParam
                    ? (carInfo.deliveryAirports?.find(a => a.airport_code === airportCodeParam)?.airport_name
                        ? `${carInfo.deliveryAirports!.find(a => a.airport_code === airportCodeParam)!.airport_name} (${airportCodeParam})`
                        : airportCodeParam)
                    : (deliveryAddress || t("checkout.delivery_to_address", "Delivery to your address"))}
                </p>
                {!airportCodeParam && deliveryDistanceParam != null && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {t("booking.delivery_distance", "Delivery distance: {{miles}} {{unit}}", {
                      miles: deliveryDistanceParam,
                      unit: getDistanceUnitPlural(carInfo.countryCode || undefined),
                    })}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {serviceLocationRequired
                  ? t("booking.location_required", "Required — enter an address or airport to see your price.")
                  : [carInfo.city, carInfo.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>
        {typeDeliveryEnabled && (
          <CheckoutLocationEditor
            trigger={
              <button type="button" className={`shrink-0 ${actionClassName}`}>
                {currentDeliveryEngaged
                  ? t("common.change")
                  : serviceLocationRequired
                    ? t("booking.enter_location", "Enter location")
                    : t("checkout.add_delivery_optional", "Add delivery (optional)")}
              </button>
            }
            pickupCity={carInfo.city}
            pickupState={carInfo.state}
            carLatitude={carInfo.latitude ?? null}
            carLongitude={carInfo.longitude ?? null}
            countryCode={carInfo.countryCode}
            currency={quote.currency}
            deliveryEnabled={!!typeDeliveryEnabled}
            deliveryMaxRadius={carInfo.deliveryMaxRadius ?? null}
            deliveryAirports={carInfo.deliveryAirports ?? []}
            currentDistance={currentDeliveryEngaged ? deliveryDistanceParam ?? null : null}
            currentAirportCode={currentDeliveryEngaged ? airportCodeParam ?? null : null}
            currentAddress={currentDeliveryEngaged ? deliveryAddress : null}
            currentIsDelivery={currentDeliveryEngaged}
            currentLatitude={deliveryLatitude}
            currentLongitude={deliveryLongitude}
            requireDelivery={serviceLocationRequired}
            onApply={onApplyLocation}
          />
        )}
      </div>
      {(() => {
        if (serviceLocationRequired && !currentDeliveryEngaged) return null;
        // Show delivery location when the renter has chosen delivery and
        // we have resolved coords — that's where they'll meet the owner.
        // Fall back to the car's home location for self-pickup.
        const useDelivery = currentDeliveryEngaged && deliveryLatitude != null && deliveryLongitude != null;
        const mapLat = useDelivery ? deliveryLatitude! : carInfo.latitude;
        const mapLng = useDelivery ? deliveryLongitude! : carInfo.longitude;
        if (mapLat == null || mapLng == null) return null;
        const mapContent = (
          <>
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <SectionErrorBoundary name="CheckoutPickupMap">
                <AppleMap
                  latitude={mapLat}
                  longitude={mapLng}
                  showSinglePin
                  pinImageUrl={carInfo.imageUrl ?? undefined}
                  height="160px"
                  interactive={false}
                />
              </SectionErrorBoundary>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {useDelivery
                ? t("checkout.delivery_location_note")
                : t("checkout.approximate_pickup_note")}
            </p>
          </>
        );
        // Use the actual map eligibility above: never offer an empty
        // disclosure for a missing service address or unknown coordinates.
        // Lazy mounting avoids initializing the map in a zero-sized panel.
        return collapsibleMap ? (
          <details className="mt-1.5" onToggle={e => setMapOpen(e.currentTarget.open)}>
            <summary className="min-h-9 cursor-pointer py-2 text-xs font-medium text-foreground underline underline-offset-2">
              {t("checkout.pickup_dropoff")}
            </summary>
            {mapOpen ? mapContent : null}
          </details>
        ) : mapContent;
      })()}
    </div>
  );
}
